import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  useToast,
} from "@chakra-ui/react";
import React, { useCallback, useContext } from "react";
import { useForm } from "react-hook-form";
import { AuthContext } from "../../providers";
import { Trans, useTranslation } from "react-i18next";

/**
 * Renders the login form
 * 
 * @returns The login form
 */
const LoginForm = () => {
  const { t } = useTranslation();
  const { signIn, forgetPassword } = useContext(AuthContext);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm();

  /**
   * Handles the form submission
   * 
   * @param {Object} values - The form values
   */
  const onSubmit = useCallback(
    (values) => {
      return new Promise((resolve) => {
        signIn(values).catch((error) => {
          if (!toast.isActive(error.message)) {
            toast({
              id: error.message,
              title: error.message,
              status: "error",
              position: "top",
              duration: 5000,
            });
          }
          resolve();
        });
      });
    },
    [signIn, toast],
  );

  const onPasswordForgotten = useCallback(async () => {
    const credentials = getValues()

    if (!credentials?.username) {
      toast({
        title: t("login.passwordForgottenError"),
        status: "error",
        position: "top",
        duration: 5000,
      })
    } else {
      try {
        await forgetPassword(credentials)
      } catch (error) {
        toast({
          title: error.message,
          status: "error",
          position: "top",
          duration: 5000,
        })
      }
    }
  }, [getValues, toast, t, forgetPassword])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack alignItems={"start"} gap={4}>
        <FormControl isInvalid={errors.username}>
          <FormLabel htmlFor={"username"}>
            <Trans>login.username.title</Trans>
          </FormLabel>
          <Input
            backgroundColor={"white"}
            htmlSize={100}
            id={"username"}
            placeholder={t("login.username.placeholder")}
            size={"sm"}
            type={"text"}
            {...register("username", {
              required: t("login.username.required"),
            })}
          />
          <FormErrorMessage>
            {errors.username && errors.username.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={errors.password}>
          <FormLabel htmlFor={"password"}>
            <Trans>login.password.title</Trans>
          </FormLabel>
          <Input
            backgroundColor={"white"}
            htmlSize={100}
            id={"password"}
            placeholder={t("login.password.placeholder")}
            size={"sm"}
            type={"password"}
            {...register("password", {
              required: t("login.password.required"),
            })}
          />
          <FormErrorMessage>
            {errors.password && errors.password.message}
          </FormErrorMessage>
        </FormControl>
        <Button
          id={"signinButton"}
          alignSelf={"stretch"}
          isLoading={isSubmitting}
          size={"sm"}
          type={"submit"}
          variant={"secondary"}
        >
          <Trans>login.signIn</Trans>
        </Button>
        <Button id={"passwordForgot"} variant={"link"} size={"md"} color={"white"} onClick={onPasswordForgotten}>
          <Trans>login.passwordForgotten</Trans>
        </Button>
      </VStack>
    </form>
  );
};

export default React.memo(LoginForm);
